import { useCallback } from 'react'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange, InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { BuyShippingModelType } from '@teinor/erp/types/company/order/buy/buyShipping'
import { BuyOrderTypeShippingKeys } from '@teinor/erp/types/company/order/buy/buyOrder'
import useBuyTypeShippingDict from '../../../../../../../utils/hooks/buys/useBuyTypeShipping'
import { getBuyShippingPriceField } from './functions'
import { flushSync } from 'react-dom'
import { getConfigParam } from '../../../../../../../utils/reducers/getters'
import { GroupVatPayload } from '../../../../../../../utils/reducers/globals/groupVats'


type BuyStepHeaderShippingProps = InputTableLeftTabsProps & BuyStepHeaderShippingLogicProps & {
    groupVats: GroupVatPayload
}

export type BuyOrderShippingModelTypeExtended = BuyShippingModelType & {
    typeShipping: BuyOrderTypeShippingKeys
    GroupVatId: number
}

const BuyStepHeaderShipping: React.FC<BuyStepHeaderShippingProps> = ({ headerData, groupVats, ...logic }) => {
    const { t } = useTwinTranslation()
    const { dictTypeShipping, deciderHandleChange, dynamicFormData, defaultBuyShippingGroupVat, updateBuyShippingBack, handleChangeVatBuyShipping } = useBuyStepHeaderShippingLogic({ headerData, ...logic })
    const extraElements: FormElement<keyof BuyShippingModelType>[] = []
    if (dynamicFormData.existsBuyShipping) {
        extraElements.push(
            {
                name: 'priceVatIncluded',
                component: 'CheckboxMargin',
                label: t('priceWithVat', 'Precio con iva'),
                checked: headerData?.OrderShipping?.priceVatIncluded,
                className: returnClassOfSpanTemplate(6)
            }
        )
        const fieldPrice = headerData.OrderShipping?.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax'
        extraElements.push(
            {
                name: fieldPrice, 
                component: 'InputNumberWithLabelMargin',
                label: t('priceShipping', 'Precio transporte'),
                value: headerData?.OrderShipping?.[fieldPrice] || 0,
                onChange:(value)=>deciderHandleChange(headerData?.OrderShipping?.id, fieldPrice, value),
                onBlur: () => updateBuyShippingBack(fieldPrice),
                className: returnClassOfSpanTemplate(6)
            },
            {
                name: 'trackingId',
                component: 'InputWithLabelMargin',
                label: t('trackingNumber', 'N. seguimiento'),
                value: headerData?.OrderShipping?.trackingId || '',
                onChange: (value) => deciderHandleChange(headerData?.OrderShipping?.id, 'trackingId', value),
                onBlur: () => updateBuyShippingBack('trackingId'),
                className: returnClassOfSpanTemplate(6)
            },
            {
                name: 'GroupVatId',
                label: t('groupVat', 'Grupo de impuestos'),
                component: 'CustomSelectWithSearchBar',
                items: groupVats || {},
                value: headerData?.OrderShipping?.GroupVatId || defaultBuyShippingGroupVat,
                onChange: handleChangeVatBuyShipping,
                required: true,
                className: returnClassOfSpanTemplate(6)
            },
        )
    }
    const sections: FormRender<BuyOrderShippingModelTypeExtended> = [
        {
            cols: 6,
            elements: [
                {
                    name: 'typeShipping',
                    label: t('typeShipping', 'Tipo de envío'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictTypeShipping,
                    required: true,
                    className: returnClassOfSpanTemplate(6)
                },
                ...extraElements,
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm handleChange={deciderHandleChange} form={sections} headerData={headerData}  />
    )
}

interface BuyStepHeaderShippingLogicProps {
    setCompleteData: SetCompleteDataTy
    headerData: TwinDictionary
    handleChange: InputTableLeftColumnChange
    getCompleteData: () => void
    orderHeaderURLs: OrderHeaderURLs
}

const useBuyStepHeaderShippingLogic = ({ setCompleteData, headerData, handleChange, getCompleteData, orderHeaderURLs }: BuyStepHeaderShippingLogicProps) => {
    const defaultBuyShippingGroupVat = getConfigParam('buyShippingGroupVatId')
    const dictTypeShipping = useBuyTypeShippingDict()
    const dynamicFormData = useDynamicFormData(headerData)
    const handleChangeBuyShipping: InputTableLeftColumnChange = useCallback(async (id: any, field: string, value: string | number) => {
        if ((field === 'initialPriceWithoutTax' || field === 'weight' || field === 'finalPriceWithTax') && value ===''){
            value = 0
        }
        setCompleteData((old: any) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData.header['OrderShipping'] = { ...myData.header['OrderShipping'], [field]: value }
            return { ...myData }
        })
    }, [setCompleteData])

    const updateBuyShippingBack = useCallback(async (field: string) => {
        await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
            id: headerData?.OrderShippingId,
            'OrderId': headerData?.id,
            [field]: headerData.OrderShipping[field]
        })
    }, [headerData, orderHeaderURLs.updateShipping])

    const handleChangeBuyShippingPriceVat = useCallback(async (id: any, value: boolean) => {
        await flushSync(async () => {
            const { newPriceField, oldPriceField } = getBuyShippingPriceField(value)
            let newPrice = 0
            await setCompleteData((old: any) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                newPrice = myData.header['OrderShipping'][oldPriceField]
                myData.header['OrderShipping'] = { ...myData.header['OrderShipping'], priceVatIncluded: value, [newPriceField]: newPrice }
                return { ...myData }
            })
            await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
                id: headerData?.OrderShippingId,
                'OrderId': headerData?.id,
                priceVatIncluded: value,
                [newPriceField] : newPrice,
            })
        })
    }, [setCompleteData, headerData.id, headerData?.OrderShippingId, orderHeaderURLs.updateShipping])

    const handleCreateBuyShipping = useCallback(async (field: string, value: any) => {
        const result = await twinFetchPostJSON(orderHeaderURLs.createShipping, { 'OrderId': headerData?.id, [field]: value, CurrencyId: headerData.CurrencyId, typePrice: 1, GroupVatId: defaultBuyShippingGroupVat})
        if (result) {
            setCompleteData((old: any) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header['OrderShipping'] = result
                myData.header['OrderShippingId'] = result.id
                return { ...myData }
            })
        }
    }, [setCompleteData, headerData, defaultBuyShippingGroupVat, orderHeaderURLs.createShipping])

    const handleChangeVatBuyShipping = useCallback(async (value: any) => {
        const result = await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
            id: headerData?.OrderShippingId,
            'OrderId': headerData?.id,
            'GroupVatId': value
        })
        if (result) {
            getCompleteData()
        }
    }, [headerData, getCompleteData, orderHeaderURLs.updateShipping])


    const deciderHandleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        if (field === 'priceVatIncluded') {
            await handleChangeBuyShippingPriceVat(id, value)
        } else if(field !== 'typeShipping'){
            if(headerData?.OrderShipping?.id) {
                await handleChangeBuyShipping(headerData?.OrderShippingId, field, value)
            }
        } else {
            await handleChange(id, field, value)
            if (String(value) === '0' && !headerData?.OrderShipping?.id) {
                await handleCreateBuyShipping(field, value)
                getCompleteData()
            }else {
                setCompleteData((old) => {
                    if (!old) {
                        return null
                    }
                    const myData = JSON.parse(JSON.stringify(old))
                    myData.header['OrderShipping'] = undefined
                    myData.header['OrderShippingId'] = undefined
                    return { ...myData }
                })
            }
        }
    }, [headerData, handleChange, setCompleteData, handleChangeBuyShipping, handleCreateBuyShipping, getCompleteData, handleChangeBuyShippingPriceVat])


    return { dictTypeShipping, deciderHandleChange, dynamicFormData, defaultBuyShippingGroupVat, updateBuyShippingBack, handleChangeVatBuyShipping}
}
const useDynamicFormData = (headerData: TwinDictionary) => {
    return {
        existsBuyShipping: headerData?.typeShipping === 0,
    }
}

export default BuyStepHeaderShipping